import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Box,
  Avatar,
  TextField,
  Alert,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Lock as LockIcon,
  Groups as GroupsIcon,
  AttachMoney as MoneyIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

interface Tanda {
  id: number;
  name: string;
  status: string;
  current_members: number;
  member_count: number;
  contribution_amount: number;
  total_amount: number;
  frequency: string;
  start_date: string;
}

interface Payment {
  id: number;
  amount: number;
  status: string;
  payment_date: string | null;
  round_id: number;
  user_id: number;
}

const profileSchema = z.object({
  email: z.string().email('Invalid email address'),
  username: z.string()
    .min(3, 'Username must be at least 3 characters')
    .max(20, 'Username must be less than 20 characters'),
  full_name: z.string().min(1, 'Full name is required'),
  phone: z.string().optional(),
  address: z.string().optional(),
});

type ProfileData = z.infer<typeof profileSchema>;

const passwordSchema = z.object({
  current_password: z.string().min(1, 'Current password is required'),
  new_password: z.string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number'),
  confirm_password: z.string(),
}).refine((data) => data.new_password === data.confirm_password, {
  message: "Passwords don't match",
  path: ["confirm_password"],
});

type PasswordData = z.infer<typeof passwordSchema>;

const Profile: React.FC = () => {
  const { user, updateUser } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [changePasswordDialog, setChangePasswordDialog] = useState(false);
  const [userTandas, setUserTandas] = useState<Tanda[]>([]);
  const [userPayments, setUserPayments] = useState<Payment[]>([]);

  const {
    control: profileControl,
    handleSubmit: handleProfileSubmit,
    reset: resetProfile,
    formState: { errors: profileErrors },
  } = useForm<ProfileData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      email: user?.email || '',
      username: user?.username || '',
      full_name: user?.full_name || '',
      phone: user?.phone || '',
      address: user?.address || '',
    },
  });

  const {
    control: passwordControl,
    handleSubmit: handlePasswordSubmit,
    reset: resetPassword,
    formState: { errors: passwordErrors },
  } = useForm<PasswordData>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      current_password: '',
      new_password: '',
      confirm_password: '',
    },
  });

  useEffect(() => {
    if (user) {
      resetProfile({
        email: user.email,
        username: user.username,
        full_name: user.full_name,
        phone: user.phone || '',
        address: user.address || '',
      });
      fetchUserData();
    }
  }, [user]);

  const fetchUserData = async () => {
    try {
      // Fetch user's tandas
      const tandasResponse = await axios.get('/tandas/my');
      setUserTandas(tandasResponse.data);

      // Fetch user's payments
      const paymentsResponse = await axios.get('/payments/my');
      setUserPayments(paymentsResponse.data);
    } catch (error) {
      console.error('Failed to fetch user data:', error);
    }
  };

  const handleProfileUpdate = async (data: ProfileData) => {
    try {
      setLoading(true);
      const response = await axios.put('/users/me', data);
      updateUser(response.data);
      enqueueSnackbar('Profile updated successfully!', { variant: 'success' });
      setEditMode(false);
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.detail || 'Failed to update profile', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (data: PasswordData) => {
    try {
      setLoading(true);
      await axios.put('/users/me/password', {
        current_password: data.current_password,
        new_password: data.new_password,
      });
      enqueueSnackbar('Password changed successfully!', { variant: 'success' });
      setChangePasswordDialog(false);
      resetPassword();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.detail || 'Failed to change password', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'forming': return 'warning';
      case 'completed': return 'info';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'success';
      case 'pending': return 'warning';
      case 'late': return 'error';
      default: return 'default';
    }
  };

  return (
    <Container maxWidth="lg">
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {t('profile.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('profile.subtitle')}
        </Typography>
      </Box>

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Left Column - Profile Info */}
        <Grid item xs={12} md={4}>
          {/* Profile Card */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
              <Avatar
                sx={{
                  width: 100,
                  height: 100,
                  mb: 2,
                  bgcolor: 'primary.main',
                  fontSize: 40,
                }}
              >
                {user?.full_name?.charAt(0).toUpperCase()}
              </Avatar>
              <Typography variant="h5">
                {user?.full_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                @{user?.username}
              </Typography>
              <Chip
                label={user?.role.toUpperCase()}
                size="small"
                color="primary"
                sx={{ mt: 1 }}
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            <List>
              <ListItem>
                <ListItemIcon>
                  <EmailIcon color="action" />
                </ListItemIcon>
                <ListItemText
                  primary={t('profile.email')}
                  secondary={user?.email}
                />
              </ListItem>

              {user?.phone && (
                <ListItem>
                  <ListItemIcon>
                    <PhoneIcon color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('profile.phone')}
                    secondary={user.phone}
                  />
                </ListItem>
              )}

              {user?.address && (
                <ListItem>
                  <ListItemIcon>
                    <LocationIcon color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('profile.address')}
                    secondary={user.address}
                  />
                </ListItem>
              )}

              <ListItem>
                <ListItemIcon>
                  <CalendarIcon color="action" />
                </ListItemIcon>
                <ListItemText
                  primary={t('profile.memberSince')}
                  secondary={user ? format(new Date(user.created_at), 'MMMM yyyy') : ''}
                />
              </ListItem>
            </List>

            <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<EditIcon />}
                onClick={() => setEditMode(true)}
              >
                {t('profile.editProfile')}
              </Button>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<LockIcon />}
                onClick={() => setChangePasswordDialog(true)}
              >
                {t('profile.changePassword')}
              </Button>
            </Box>
          </Paper>

          {/* Stats Card */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('profile.accountStats')}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h4" color="primary">
                      {userTandas.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('profile.tandas')}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={6}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h4" color="success">
                      {userPayments.filter(p => p.status === 'paid').length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('profile.paymentsMade')}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={6}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h4" color="warning">
                      {userPayments.filter(p => p.status === 'pending').length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('profile.pendingPayments')}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={6}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h4" color="info">
                      {userTandas.filter(t => t.status === 'completed').length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('profile.completedTandas')}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Right Column - Tabs Content */}
        <Grid item xs={12} md={8}>
          <Paper>
            <Tabs
              value={tabValue}
              onChange={(_, newValue) => setTabValue(newValue)}
              variant="fullWidth"
            >
              <Tab label={t('profile.profileTab')} />
              <Tab label={t('profile.myTandasTab')} />
              <Tab label={t('profile.paymentHistoryTab')} />
              <Tab label={t('profile.activityTab')} />
            </Tabs>

            <Box sx={{ p: 3 }}>
              {/* Profile Tab */}
              {tabValue === 0 && (
                <Box>
                  {editMode ? (
                    <Box component="form" onSubmit={handleProfileSubmit(handleProfileUpdate)}>
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <Controller
                            name="full_name"
                            control={profileControl}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label={t('profile.fullName')}
                                variant="outlined"
                                fullWidth
                                error={!!profileErrors.full_name}
                                helperText={profileErrors.full_name?.message}
                                InputProps={{
                                  startAdornment: (
                                    <PersonIcon color="action" sx={{ mr: 1 }} />
                                  ),
                                }}
                                disabled={loading}
                              />
                            )}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <Controller
                            name="username"
                            control={profileControl}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label={t('profile.username')}
                                variant="outlined"
                                fullWidth
                                error={!!profileErrors.username}
                                helperText={profileErrors.username?.message}
                                disabled={loading}
                              />
                            )}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <Controller
                            name="email"
                            control={profileControl}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label={t('profile.email')}
                                type="email"
                                variant="outlined"
                                fullWidth
                                error={!!profileErrors.email}
                                helperText={profileErrors.email?.message}
                                InputProps={{
                                  startAdornment: (
                                    <EmailIcon color="action" sx={{ mr: 1 }} />
                                  ),
                                }}
                                disabled={loading}
                              />
                            )}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <Controller
                            name="phone"
                            control={profileControl}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label={t('profile.phone')}
                                variant="outlined"
                                fullWidth
                                error={!!profileErrors.phone}
                                helperText={profileErrors.phone?.message}
                                InputProps={{
                                  startAdornment: (
                                    <PhoneIcon color="action" sx={{ mr: 1 }} />
                                  ),
                                }}
                                disabled={loading}
                              />
                            )}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <Controller
                            name="address"
                            control={profileControl}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label={t('profile.address')}
                                variant="outlined"
                                fullWidth
                                multiline
                                rows={2}
                                error={!!profileErrors.address}
                                helperText={profileErrors.address?.message}
                                InputProps={{
                                  startAdornment: (
                                    <LocationIcon color="action" sx={{ mr: 1 }} />
                                  ),
                                }}
                                disabled={loading}
                              />
                            )}
                          />
                        </Grid>
                      </Grid>

                      <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                        <Button
                          type="submit"
                          variant="contained"
                          startIcon={<SaveIcon />}
                          disabled={loading}
                        >
                          {t('profile.saveChanges')}
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setEditMode(false);
                            resetProfile();
                          }}
                          disabled={loading}
                        >
                          {t('profile.cancelEdit')}
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <Box>
                      <Alert severity="info" sx={{ mb: 3 }}>
                        {t('profile.profileInfo')}
                      </Alert>

                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label={t('profile.fullName')}
                            value={user?.full_name || ''}
                            fullWidth
                            disabled
                            InputProps={{
                              startAdornment: (
                                <PersonIcon color="action" sx={{ mr: 1 }} />
                              ),
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            label={t('profile.username')}
                            value={user?.username || ''}
                            fullWidth
                            disabled
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            label={t('profile.email')}
                            value={user?.email || ''}
                            fullWidth
                            disabled
                            InputProps={{
                              startAdornment: (
                                <EmailIcon color="action" sx={{ mr: 1 }} />
                              ),
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            label={t('profile.phone')}
                            value={user?.phone || t('profile.notProvided')}
                            fullWidth
                            disabled
                            InputProps={{
                              startAdornment: (
                                <PhoneIcon color="action" sx={{ mr: 1 }} />
                              ),
                            }}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            label={t('profile.address')}
                            value={user?.address || t('profile.notProvided')}
                            fullWidth
                            multiline
                            rows={2}
                            disabled
                            InputProps={{
                              startAdornment: (
                                <LocationIcon color="action" sx={{ mr: 1 }} />
                              ),
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                </Box>
              )}

              {/* My Tandas Tab */}
              {tabValue === 1 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {t('profile.myTandas')} ({userTandas.length})
                  </Typography>

                  {userTandas.length === 0 ? (
                    <Alert severity="info">
                      {t('profile.noTandas')}
                    </Alert>
                  ) : (
                    <List>
                      {userTandas.map((tanda) => (
                        <ListItem
                          key={tanda.id}
                          secondaryAction={
                            <Chip
                              label={tanda.status.toUpperCase()}
                              size="small"
                              color={getStatusColor(tanda.status) as any}
                            />
                          }
                          sx={{
                            mb: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                          }}
                        >
                          <ListItemIcon>
                            <GroupsIcon />
                          </ListItemIcon>
                          <ListItemText
                            primary={tanda.name}
                            secondary={
                              <Box>
                                <Typography variant="body2" component="span">
                                  ${tanda.contribution_amount} MXN • {tanda.current_members}/{tanda.member_count} {t('tandaDetail.members').toLowerCase()}
                                </Typography>
                                <br />
                                <Typography variant="caption" color="text.secondary">
                                  {format(new Date(tanda.start_date), 'MMM dd, yyyy')}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              )}

              {/* Payment History Tab */}
              {tabValue === 2 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {t('profile.paymentHistory')} ({userPayments.length})
                  </Typography>

                  {userPayments.length === 0 ? (
                    <Alert severity="info">
                      {t('profile.noPayments')}
                    </Alert>
                  ) : (
                    <List>
                      {userPayments.map((payment) => (
                        <ListItem
                          key={payment.id}
                          secondaryAction={
                            <Chip
                              label={payment.status}
                              size="small"
                              color={getPaymentStatusColor(payment.status) as any}
                            />
                          }
                          sx={{
                            mb: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                          }}
                        >
                          <ListItemIcon>
                            <MoneyIcon />
                          </ListItemIcon>
                          <ListItemText
                            primary={`Payment #${payment.id} - $${payment.amount} MXN`}
                            secondary={
                              <Box>
                                <Typography variant="body2" component="span">
                                  {t('profile.amount')}: ${payment.amount} MXN
                                </Typography>
                                <br />
                                <Typography variant="caption" color="text.secondary">
                                  {payment.payment_date
                                    ? `${t('tandaDetail.paidOn')} ${format(new Date(payment.payment_date), 'MMM dd, yyyy')}`
                                    : t('tandaDetail.notPaidYet')}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              )}

              {/* Activity Tab */}
              {tabValue === 3 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {t('profile.recentActivity')}
                  </Typography>

                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleIcon color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Profile updated"
                        secondary="Just now"
                      />
                    </ListItem>

                    <ListItem>
                      <ListItemIcon>
                        <MoneyIcon color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Payment made"
                        secondary="Family Vacation Tanda - $100 MXN"
                      />
                    </ListItem>

                    <ListItem>
                      <ListItemIcon>
                        <GroupsIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Joined tanda"
                        secondary="Emergency Fund Tanda"
                      />
                    </ListItem>

                    <ListItem>
                      <ListItemIcon>
                        <WarningIcon color="warning" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Payment reminder"
                        secondary="Home Renovation Tanda - Due in 3 days"
                      />
                    </ListItem>

                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleIcon color="info" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Tanda completed"
                        secondary="Car Purchase Tanda successfully completed"
                      />
                    </ListItem>
                  </List>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Change Password Dialog */}
      <Dialog open={changePasswordDialog} onClose={() => setChangePasswordDialog(false)}>
        <DialogTitle>{t('profile.changePassword')}</DialogTitle>
        <Box component="form" onSubmit={handlePasswordSubmit(handlePasswordChange)}>
          <DialogContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Password requirements:</strong>
                <br />
                • At least 8 characters
                <br />
                • At least one uppercase letter
                <br />
                • At least one lowercase letter
                <br />
                • At least one number
              </Typography>
            </Alert>

            <Controller
              name="current_password"
              control={passwordControl}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('profile.currentPassword')}
                  type="password"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  error={!!passwordErrors.current_password}
                  helperText={passwordErrors.current_password?.message}
                  disabled={loading}
                />
              )}
            />

            <Controller
              name="new_password"
              control={passwordControl}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('profile.newPassword')}
                  type="password"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  error={!!passwordErrors.new_password}
                  helperText={passwordErrors.new_password?.message}
                  disabled={loading}
                />
              )}
            />

            <Controller
              name="confirm_password"
              control={passwordControl}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('profile.confirmNewPassword')}
                  type="password"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  error={!!passwordErrors.confirm_password}
                  helperText={passwordErrors.confirm_password?.message}
                  disabled={loading}
                />
              )}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setChangePasswordDialog(false)} disabled={loading}>
              {t('profile.cancelEdit')}
            </Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? t('profile.changing') : t('profile.change')}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Container>
  );
};

export default Profile;
